/**
 * What the analysis actually read — and what it did not.
 *
 * Every stage here has a limit: how many attachments are fetched, how much
 * text is kept, how many tables are probed, how many queries the model may
 * write. When one bites, the analysis is still confident and still
 * well-written; it is simply working from less than the ticket contains.
 * Nothing on screen used to say so.
 *
 * This is UI_STANDARDS §1 ("never truncate silently") applied to the whole
 * analysis rather than to one label. The rule followed here: state coverage
 * always, but only raise a *warning* when something was genuinely left out —
 * a caution that fires every time is one people learn to scroll past.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Info } from 'lucide-react';

export interface CoverageItem {
  label: string;
  /** e.g. "4 of 7" or "182,400" */
  value: string;
  /** True when this line represents content the analysis did NOT see. */
  incomplete?: boolean;
  /** Shown under the line when incomplete — what was lost and what to do. */
  detail?: string;
}

interface Props {
  items: CoverageItem[];
  /** Hard caps worth knowing even when nothing was dropped. */
  limits?: string[];
  /** Which column of the limits table to show in the help popover. */
  mode?: 'support' | 'cr';
}

interface LimitSet {
  max_documents: number;
  max_bytes: number;
  max_chars_each: number;
  max_chars_total: number;
  max_sheet_rows: number;
  max_sheets: number;
}

/** Screenshots have their own budget — they never compete with documents. */
interface ImageLimits {
  max_images: number;
  max_bytes: number;
  max_chars_total: number;
}

interface LimitsResponse {
  support: LimitSet;
  cr: LimitSet;
  images?: ImageLimits;
  notes: string[];
}

/** The limits as the RUNNING system has them.
 *
 *  Fetched, never hard-coded: every value is tunable without a deploy, so a
 *  written-in help text would drift the first time one changed — and a help
 *  panel that states the wrong limit is worse than none, because people plan
 *  around it. */
function LimitsHelp({ mode }: { mode: 'support' | 'cr' }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<LimitsResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || data || failed) return;
    fetch('/api/v1/attachment-limits')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setData)
      .catch(() => setFailed(true));
  }, [open, data, failed]);

  const n = (v: number) => v.toLocaleString();
  const set = data ? (mode === 'cr' ? data.cr : data.support) : null;

  return (
    <span className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title="What are the limits?"
        className="ml-1 w-4 h-4 rounded-full border border-current text-[9px] font-bold leading-none opacity-70 hover:opacity-100"
      >
        ?
      </button>
      {open && (
        <div
          className="absolute z-20 left-0 mt-1 w-80 p-3 rounded-lg border border-gray-300 bg-white shadow-lg text-[11px] text-gray-700 font-normal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="font-semibold text-gray-800 mb-1.5">
            What {mode === 'cr' ? 'CR Analysis' : 'Issue Analysis'} reads
          </div>
          {failed && <p className="text-gray-500">Limits unavailable right now.</p>}
          {!failed && !set && <p className="text-gray-400">Loading…</p>}
          {set && (
            <table className="w-full mb-2">
              <tbody>
                <tr><td className="pr-2 text-gray-500">Documents per ticket</td><td className="font-medium tabular-nums">{n(set.max_documents)}</td></tr>
                <tr><td className="pr-2 text-gray-500">Text from each</td><td className="font-medium tabular-nums">{n(set.max_chars_each)} chars</td></tr>
                <tr><td className="pr-2 text-gray-500">Text from all combined</td><td className="font-medium tabular-nums">{n(set.max_chars_total)} chars</td></tr>
                <tr><td className="pr-2 text-gray-500">Spreadsheet rows / sheet</td><td className="font-medium tabular-nums">{n(set.max_sheet_rows)}</td></tr>
                <tr><td className="pr-2 text-gray-500">Sheets per workbook</td><td className="font-medium tabular-nums">{n(set.max_sheets)}</td></tr>
                <tr><td className="pr-2 text-gray-500">Size per file</td><td className="font-medium tabular-nums">{(set.max_bytes / 1_048_576).toFixed(0)} MB</td></tr>
              </tbody>
            </table>
          )}
          {data?.images && (
            <table className="w-full mb-2 border-t border-gray-200 pt-1">
              <tbody>
                <tr><td className="pr-2 text-gray-500" colSpan={2}><span className="font-semibold text-gray-700">Screenshots</span> — a separate budget</td></tr>
                <tr><td className="pr-2 text-gray-500">Images per ticket</td><td className="font-medium tabular-nums">{n(data.images.max_images)}</td></tr>
                <tr><td className="pr-2 text-gray-500">Text read from all</td><td className="font-medium tabular-nums">{n(data.images.max_chars_total)} chars</td></tr>
                <tr><td className="pr-2 text-gray-500">Size per image</td><td className="font-medium tabular-nums">{(data.images.max_bytes / 1_048_576).toFixed(0)} MB</td></tr>
              </tbody>
            </table>
          )}
          {data?.notes?.length ? (
            <ul className="space-y-0.5 border-t border-gray-200 pt-1.5 text-gray-600">
              {data.notes.map((t) => (
                <li key={t}>• {t}</li>
              ))}
            </ul>
          ) : null}
          <p className="mt-1.5 text-[10px] text-gray-400">
            These are the values in force now. Ask the LISA team if a ticket needs more.
          </p>
        </div>
      )}
    </span>
  );
}

/** Minimal shape shared by the Issue and CR extraction results. */
export interface ExtractionLike {
  filename: string;
  status: string;
  detail?: string | null;
}

/**
 * One coverage line per REASON a file was not read — never one lumped line.
 *
 * Files are skipped for unrelated causes: an unreadable format, or simply
 * being past the document cap. Reporting them together forced a single
 * explanation onto all of them, so nine files skipped for hitting the cap
 * were labelled "old Word files — save them as .docx", advice that would
 * have changed nothing for them. A wrong reason is worse than no reason,
 * because the reader acts on it.
 */
export function attachmentCoverage(results: ExtractionLike[]): CoverageItem[] {
  if (results.length === 0) return [];
  const ok = results.filter((r) => r.status === 'success').length;
  const bad = results.filter((r) => r.status !== 'success');

  // The ratio is a SUMMARY of the skips listed below it, not a separate
  // problem. Marking it incomplete counted one issue twice, so a CR with two
  // unreadable files and three unconfirmed items announced "3 limits
  // reached" when there were two. The reason lines carry the warning.
  const items: CoverageItem[] = [
    { label: 'Attachments read', value: `${ok} of ${results.length}` },
  ];

  // Group by the reason the backend gave, so each group can carry the
  // action that actually resolves it.
  const byReason: Record<string, string[]> = {};
  for (const b of bad) {
    const reason = (b.detail || 'not read').trim();
    byReason[reason] = [...(byReason[reason] ?? []), b.filename];
  }

  for (const [reason, files] of Object.entries(byReason)) {
    const capped = /cap reached/i.test(reason);
    const legacyDoc = /\.doc\b/i.test(reason) && /not read/i.test(reason);
    items.push({
      label: capped ? 'Skipped — limit' : legacyDoc ? 'Skipped — format' : 'Skipped',
      value: `${files.length} file${files.length === 1 ? '' : 's'}`,
      incomplete: true,
      detail: `${files.join(', ')} — ${
        capped
          ? 'the attachment limit was reached, so these were never opened regardless of type. Ask an administrator to raise the limit, or attach the important documents to a fresh ticket.'
          : legacyDoc
            ? 'old Word format cannot be read. Save each as .docx and re-attach, then re-run.'
            : reason
      }`,
    });
  }
  return items;
}

export function CoverageNotice({ items, limits = [], mode = 'support' }: Props) {
  const [open, setOpen] = useState(false);
  const gaps = items.filter((i) => i.incomplete);
  if (items.length === 0) return null;

  const warn = gaps.length > 0;

  return (
    <div
      className={`rounded-lg border text-xs ${
        warn ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 px-3 py-2 text-left"
      >
        {warn ? (
          <AlertTriangle size={13} className="text-amber-600 shrink-0" />
        ) : (
          <Info size={13} className="text-gray-400 shrink-0" />
        )}
        <span className={`font-semibold ${warn ? 'text-amber-900' : 'text-gray-600'}`}>
          {warn
            ? `Partial analysis — ${gaps.length} limit${gaps.length === 1 ? '' : 's'} reached`
            : 'What this analysis read'}
        </span>
        {/* Sits beside the headline so the limits are one click away exactly
            when someone is asking "why didn't it read that?" */}
        <LimitsHelp mode={mode} />
        <span className="ml-auto text-gray-400">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </span>
      </button>

      {warn && !open && (
        // The headline gap stays visible when collapsed — a warning you have
        // to expand to discover is not a warning.
        <div className="px-3 pb-2 -mt-1 text-[11px] text-amber-800">
          {gaps[0].detail ?? `${gaps[0].label}: ${gaps[0].value}`}
          {gaps.length > 1 && ` (+${gaps.length - 1} more)`}
        </div>
      )}

      {open && (
        <div className="px-3 pb-2.5 space-y-1.5">
          <table className="w-full">
            <tbody>
              {items.map((i) => (
                <tr key={i.label} className="align-top">
                  <td className="py-0.5 pr-3 text-gray-500 whitespace-nowrap">{i.label}</td>
                  <td
                    className={`py-0.5 tabular-nums font-medium ${
                      i.incomplete ? 'text-amber-800' : 'text-gray-700'
                    }`}
                  >
                    {i.value}
                    {i.incomplete && i.detail && (
                      <div className="font-normal text-[11px] text-amber-700 mt-0.5">
                        {i.detail}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {limits.length > 0 && (
            <div className="pt-1.5 border-t border-gray-200 text-[11px] text-gray-500">
              <span className="font-medium">Always applied: </span>
              {limits.join(' · ')}
            </div>
          )}
          <p className="text-[10px] text-gray-400 pt-0.5">
            A conclusion can only rest on what was read. Where a limit was reached, treat the
            analysis as a lead rather than a complete account.
          </p>
        </div>
      )}
    </div>
  );
}
